import dataValidation, { DataValidationSheetOptions } from '@onparallel/write-excel-file-data-validation';
import { isEqual, toPairs } from 'es-toolkit/compat';
import { ListField } from 'interfaces/genericFields';
import { Procedure } from 'interfaces/procedure';
import { cancerTypeField } from 'interfaces/procedure/fields/caseFields';
import { ProceduresFieldsContext } from 'interfaces/procedure/fields/helpers';
import {
  biopsySiteField,
  biopsyTypeField,
  preservationMethodField,
  procedureTypeField,
  stainTypeField,
} from 'interfaces/procedure/fields/slideFields';
import { Slide } from 'interfaces/slide';
import writeXlsxFile, { Sheet, SheetData } from 'write-excel-file/browser';
import { MANIFEST_HEADERS, NEW_CASE_HEADER } from './consts';
import { ManifestCaseTarget, ManifestDraftCase } from './dialogState';
import { getCaseFromRowCombinedWithRealData } from './helpers';
import { MANIFEST_WORKBOOK_INSTRUCTIONS } from './manifestWorkbookInstructions';

type ManifestWorkbookValue = string | number | boolean | null;
export type ManifestWorkbookRows = readonly (readonly ManifestWorkbookValue[])[];

export const NEW_CASE_COLUMN_INPUT_TITLE = 'Case assignment';
export const NEW_CASE_COLUMN_INPUT =
  'Leave blank to select the saved case with this label. Choose New case to create a case. Any other value is invalid.';

export interface ManifestWorkbookReference {
  column: number;
  values: readonly string[];
  inputTitle?: string;
  input?: string;
  headerPrompt?: boolean;
}

export interface ManifestWorkbookInput {
  rows: ManifestWorkbookRows;
  instructions?: readonly string[];
  references?: readonly ManifestWorkbookReference[];
  context?: ManifestWorkbookRows;
  recovery?: ManifestWorkbookRows;
  validationRowCount?: number;
}

export const MANIFEST_RECOVERY_HEADERS = ['Case editor ID', 'Slide ID', 'Field', 'Draft value', 'Issue and next step'];

interface ManifestWorkbookProjectionIssue {
  caseId: number;
  slideId: string | null;
  field: string;
  message: string;
}

interface ManifestWorkbookPlanInput {
  cases: readonly ManifestDraftCase[];
  sourceCases: readonly Procedure[];
  existingCases: readonly Procedure[];
  targetsBySlideId: Readonly<Record<string, ManifestCaseTarget>>;
  procedureFieldContext: ProceduresFieldsContext;
}

interface ManifestWorkbookProjection {
  rows: ManifestWorkbookRows;
  issues: ManifestWorkbookProjectionIssue[];
  recovery: ManifestWorkbookRows;
}

const INHERITED_SLIDE_FIELDS = {
  stainingType: 'Stain Type',
  biopsySiteId: 'Biopsy Site',
  biopsyType: 'Biopsy Type',
  preservationMethod: 'Preservation Method',
  procedureType: 'Procedure Type',
  metastasis: 'Metastasis (Y/N)',
  positiveControl: 'Positive Control on Slide (Y/N)',
  negativeControl: 'Negative Control on Slide (Y/N)',
} as const satisfies Partial<Record<keyof Slide, string>>;

const INHERITED_SLIDE_FIELD_ENTRIES = toPairs(INHERITED_SLIDE_FIELDS) as [
  keyof typeof INHERITED_SLIDE_FIELDS,
  string
][];

const isBlankValue = (value: unknown) => value == null || (typeof value === 'string' && value.trim() === '');
const toIdentifierCell = (value: string | number | null | undefined) => (value == null ? null : String(value));
const toBooleanCell = (value: boolean | null | undefined) => (value == null ? null : value ? 'Y' : 'N');

export const buildManifestWorkbookReferences = (
  context: ProceduresFieldsContext
): NonNullable<ManifestWorkbookInput['references']> => {
  const fields: {
    header: string;
    field: Pick<ListField<unknown, ProceduresFieldsContext>, 'label' | 'enumType' | 'optionsGetter' | 'options'>;
    loading?: boolean;
  }[] = [
    { header: INHERITED_SLIDE_FIELDS.stainingType, field: stainTypeField },
    { header: 'Cancer Type', field: cancerTypeField, loading: context.allCancerTypesLoading },
    { header: INHERITED_SLIDE_FIELDS.biopsySiteId, field: biopsySiteField },
    { header: INHERITED_SLIDE_FIELDS.biopsyType, field: biopsyTypeField, loading: context.uiSettingsLoading },
    {
      header: INHERITED_SLIDE_FIELDS.preservationMethod,
      field: preservationMethodField,
      loading: context.uiSettingsLoading,
    },
    { header: INHERITED_SLIDE_FIELDS.procedureType, field: procedureTypeField, loading: context.uiSettingsLoading },
  ];
  return [
    {
      column: MANIFEST_HEADERS.indexOf(NEW_CASE_HEADER),
      values: ['', 'New case'],
      inputTitle: NEW_CASE_COLUMN_INPUT_TITLE,
      input: NEW_CASE_COLUMN_INPUT,
      headerPrompt: true,
    },
    ...fields.map(({ header, field, loading }) => {
      const options = loading
        ? []
        : field.enumType
        ? context.enumDisplayNames?.[field.enumType] ?? []
        : field.optionsGetter?.(context) ?? field.options ?? [];
      return { column: MANIFEST_HEADERS.indexOf(header), values: options.map(({ label }) => label) };
    }),
    ...(
      [
        INHERITED_SLIDE_FIELDS.metastasis,
        INHERITED_SLIDE_FIELDS.positiveControl,
        INHERITED_SLIDE_FIELDS.negativeControl,
      ] as const
    ).map((header) => ({
      column: MANIFEST_HEADERS.indexOf(header),
      values: [toBooleanCell(true), toBooleanCell(false)] as string[],
    })),
  ];
};

export const projectManifestWorkbookPlan = ({
  cases,
  sourceCases,
  existingCases,
  targetsBySlideId,
  procedureFieldContext,
}: ManifestWorkbookPlanInput): ManifestWorkbookProjection => {
  const sourceBySlideId = new Map<string, { procedure: Procedure; slide: Slide }>();
  for (const procedure of sourceCases) {
    for (const slide of procedure.slides ?? []) {
      if (!sourceBySlideId.has(slide.id)) sourceBySlideId.set(slide.id, { procedure, slide });
    }
  }
  const targetsByLabel = new Map<string | undefined, Set<number>>();
  for (const procedure of existingCases) {
    const identities = targetsByLabel.get(procedure.label) ?? new Set<number>();
    identities.add(procedure.id);
    targetsByLabel.set(procedure.label, identities);
  }
  const issues: ManifestWorkbookProjectionIssue[] = [];
  const rows: ManifestWorkbookValue[][] = [[...MANIFEST_HEADERS]];
  const recovery: ManifestWorkbookValue[][] = [];
  for (const procedure of cases) {
    const caseValues = {
      label: procedure.label,
      cancerTypeId: procedure.cancerTypeId,
      cancerSubtypes: procedure.cancerSubtypes,
    };
    const addIssue = (slideId: string | null, field: string, message: string, value: unknown) => {
      issues.push({ caseId: procedure.id, slideId, field, message });
      recovery.push([procedure.id, slideId, field, JSON.stringify({ value }), message]);
    };
    if (!procedure.slides?.length) {
      addIssue(null, 'Slides', 'This case has no slides. Add a slide to this case in the case assignment.', {
        ...caseValues,
        slides: [],
      });
    }
    for (const slide of procedure.slides ?? []) {
      const source = sourceBySlideId.get(slide.id);
      const target = procedure.draftTarget === undefined ? targetsBySlideId[slide.id] : procedure.draftTarget;
      const draftValues: Record<string, unknown> = {
        'Target case': target,
        'Case ID': procedure.label,
        'Cancer Type': procedure.cancerTypeId,
        'Cancer Subtype': procedure.cancerSubtypes,
        'Slide ID': slide.id,
        'File Name': slide.originalFileName,
        ...Object.fromEntries(INHERITED_SLIDE_FIELD_ENTRIES.map(([field, header]) => [header, slide[field]])),
      };
      const addSlideIssue = (field: string, message: string) =>
        addIssue(slide.id ?? null, field, message, field === 'Metadata' ? draftValues : draftValues[field]);
      const matchingTargets = targetsByLabel.get(procedure.label);
      const savedTargetPreserved =
        typeof target === 'number' && matchingTargets?.size === 1 && matchingTargets.has(target);
      if (target == null || (target !== 'new' && !savedTargetPreserved)) {
        addSlideIssue(
          'Target case',
          target == null
            ? 'No target case is selected. Select an existing case or choose New case in the case assignment.'
            : matchingTargets && matchingTargets.size > 1
            ? 'More than one existing case has this Case ID. Select a case with a unique Case ID or choose New case.'
            : 'The selected case could not be matched to this Case ID. Select an existing case or choose New case in the case assignment.'
        );
      }
      if ((procedure.cancerSubtypes?.length ?? 0) > 1) {
        addSlideIssue(
          'Cancer Subtype',
          'This case has multiple cancer subtypes. Check and restore all subtypes in the case assignment after uploading.'
        );
      }
      const row = [
        target === 'new' ? 'New case' : null,
        procedure.label ?? null,
        slide.stainingType ?? null,
        typeof procedure.cancerTypeId === 'number'
          ? procedureFieldContext.allCancerTypes?.[procedure.cancerTypeId]?.displayName ?? null
          : toIdentifierCell(procedure.cancerTypeId),
        procedure.draftIdentifier ? procedure.draftIdentifier.slideId : slide.id ?? null,
        procedure.draftIdentifier ? procedure.draftIdentifier.fileName : slide.originalFileName ?? null,
        typeof slide.biopsySiteId === 'number'
          ? procedureFieldContext.biopsySiteTypes?.find(({ id }) => id === slide.biopsySiteId)?.displayName ?? null
          : toIdentifierCell(slide.biopsySiteId),
        procedure.cancerSubtypes?.length === 1 ? procedure.cancerSubtypes[0] : null,
        slide.biopsyType ?? null,
        slide.preservationMethod ?? null,
        slide.procedureType ?? null,
        toBooleanCell(slide.metastasis),
        toBooleanCell(slide.positiveControl),
        toBooleanCell(slide.negativeControl),
      ];
      for (const issue of procedure.draftIssues || []) {
        const header =
          issue.field === 'targetCaseAssignment'
            ? NEW_CASE_HEADER
            : INHERITED_SLIDE_FIELD_ENTRIES.find(([field]) => field === issue.field)?.[1];
        if (header && issue.rawValue !== undefined) row[MANIFEST_HEADERS.indexOf(header)] = issue.rawValue;
        addSlideIssue(header || 'Slide ID', issue.message);
      }
      if (slide.biopsySiteId != null && row[MANIFEST_HEADERS.indexOf('Biopsy Site')] == null) {
        addSlideIssue(
          INHERITED_SLIDE_FIELDS.biopsySiteId,
          'The selected biopsy site could not be found. Select an available biopsy site in the case assignment.'
        );
      }
      for (const header of ['Case ID', 'Stain Type', 'Cancer Type', 'Slide ID']) {
        const index = MANIFEST_HEADERS.indexOf(header);
        if (isBlankValue(row[index])) {
          addSlideIssue(
            header,
            header === 'Cancer Type' && !isBlankValue(procedure.cancerTypeId)
              ? 'The selected cancer type could not be found. Select an available cancer type in the case assignment.'
              : 'A required value is missing. Fill in this field in the case assignment before applying changes.'
          );
        }
      }
      try {
        const decoded = getCaseFromRowCombinedWithRealData(
          procedureFieldContext,
          MANIFEST_HEADERS.map((header) => header.toLowerCase()),
          row.map((value) => value ?? ''),
          procedure.labId,
          procedure.studyId,
          rows.length,
          source ? [source.procedure] : [],
          []
        );
        const decodedSlide = decoded.slides?.[0];
        for (const [field, header] of [
          ['label', 'Case ID'],
          ['cancerTypeId', 'Cancer Type'],
        ] as const) {
          if (!isEqual(procedure[field], decoded[field])) {
            addSlideIssue(
              header,
              'This value would change on upload. Set the intended value in the case assignment after uploading.'
            );
          }
        }
        if (
          (procedure.cancerSubtypes?.length ?? 0) <= 1 &&
          !isEqual(procedure.cancerSubtypes ?? [], decoded.cancerSubtypes ?? [])
        ) {
          addSlideIssue(
            'Cancer Subtype',
            procedure.cancerSubtypes?.length
              ? 'The cancer subtype would change on upload. Set the intended subtype in the case assignment after uploading.'
              : "Uploading would restore the case's saved cancer subtypes. Clear the subtypes in the case assignment after uploading."
          );
        }
        for (const [field, header] of INHERITED_SLIDE_FIELD_ENTRIES) {
          if (!isEqual(slide[field] ?? null, decodedSlide?.[field] ?? null)) {
            addSlideIssue(
              header,
              isBlankValue(slide[field])
                ? "Uploading would restore the slide's saved value. Clear this field in the case assignment after uploading."
                : 'This value would change on upload. Set the intended value in the case assignment after uploading.'
            );
          }
        }
        if (slide.id !== decodedSlide?.id) {
          addSlideIssue(
            'Slide ID',
            'This Slide ID would identify a different slide on upload. Select the intended slide in the case assignment.'
          );
        }
        if (source && slide.originalFileName !== decodedSlide?.originalFileName) {
          addSlideIssue(
            'File Name',
            "Uploading would restore the slide's original filename. Check that this is the intended slide in the case assignment."
          );
        }
      } catch (error) {
        addSlideIssue(
          'Metadata',
          `${
            error instanceof Error ? error.message : 'This slide has invalid or missing details.'
          } Correct this slide's details in the case assignment before applying changes.`
        );
      }
      rows.push(row);
    }
  }
  return { rows, issues, recovery: recovery.length ? [MANIFEST_RECOVERY_HEADERS, ...recovery] : [] };
};

const toSheetData = (rows: ManifestWorkbookRows): SheetData =>
  rows.map((row) => row.map((value) => (typeof value === 'string' ? { value, type: String } : value)));

const getReferenceColumnName = (index: number): string => {
  let column = index + 1;
  let name = '';
  while (column > 0) {
    name = String.fromCharCode(65 + ((column - 1) % 26)) + name;
    column = Math.floor((column - 1) / 26);
  }
  return name;
};

export const createManifestWorkbook = async ({
  rows,
  instructions = [],
  references = [],
  context = [],
  recovery = [],
  validationRowCount = rows.length,
}: ManifestWorkbookInput): Promise<Blob> => {
  for (const { column } of references) {
    if (!Number.isInteger(column) || column < 0 || typeof rows[0]?.[column] !== 'string') {
      throw new Error(`Reference column ${column} is not present in the manifest header.`);
    }
  }

  const referenceRows = [
    references.map(({ column }) => rows[0][column]),
    ...Array.from({ length: Math.max(0, ...references.map(({ values }) => values.length)) }, (_, index) =>
      references.map(({ values }) => values[index] ?? null)
    ),
  ];
  const manifestSheet: Sheet<Blob> & DataValidationSheetOptions = {
    sheet: 'Slides manifest',
    data: toSheetData(rows),
    dataValidation: references.flatMap(({ column, values, inputTitle, input, headerPrompt }, index) => {
      const prompt = inputTitle || input ? { inputTitle, input } : {};
      const columnNumber = column + 1;
      return [
        ...(headerPrompt && input
          ? [
              {
                cellRange: { from: { row: 1, column: columnNumber }, to: { row: 1, column: columnNumber } },
                validation: { type: 'any' as const, allowBlank: true, ...prompt },
              },
            ]
          : []),
        ...(values.length === 0 || validationRowCount < 2
          ? []
          : [
              {
                cellRange: {
                  from: { row: 2, column: columnNumber },
                  to: { row: validationRowCount, column: columnNumber },
                },
                validation: {
                  type: 'list' as const,
                  valuesRange: `INDIRECT("'References'!$${getReferenceColumnName(index)}$2:$${getReferenceColumnName(
                    index
                  )}$${values.length + 1}")`,
                  allowBlank: true,
                  ...prompt,
                },
              },
            ]),
      ];
    }),
  };

  return writeXlsxFile(
    [
      manifestSheet,
      { sheet: 'Instructions', data: toSheetData([['Instructions'], ...instructions.map((value) => [value])]) },
      { sheet: 'References', data: toSheetData(referenceRows) },
      { sheet: 'Context', data: toSheetData(context) },
      ...(recovery.length ? [{ sheet: 'Plan recovery', data: toSheetData(recovery) }] : []),
    ],
    {
      features: [
        dataValidation,
        {
          files: {
            transform: {
              'xl/workbook.xml': {
                transformElementAttributes: (tagName, attributes, index, sheetOptions) =>
                  tagName === 'sheet' && index !== undefined && sheetOptions[index]?.sheet === 'Context'
                    ? { ...attributes, state: 'hidden' }
                    : attributes,
              },
            },
          },
        },
      ],
    }
  ).toBlob();
};

export const createManifestTemplateWorkbook = (context: ProceduresFieldsContext): Promise<Blob> =>
  createManifestWorkbook({
    rows: [MANIFEST_HEADERS],
    instructions: MANIFEST_WORKBOOK_INSTRUCTIONS,
    references: buildManifestWorkbookReferences(context),
    validationRowCount: 1048576,
  });
